import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ReportJobBatchEntry } from "../../../../client";
import { LOB } from "../../../../api/routes";
import { useQueueJobs } from "../../rater/useIntegrationJobs";
import { ROUTES } from "../../../../routes";
import {
  useCancelJobs,
  useLocalUser,
  useSafeReports,
  useUsers,
} from "../../data";
import styles from "./shared.module.css";
import { formatTimestamp } from "./submissionHistoryUtils";

type StatusLabel = "In Progress" | "Pending" | "Partially Running";

type QueueRow = ReportJobBatchEntry & {
  company_name: string;
  triggered_by: string;
  min_position: number | null;
  position_display: string;
  status_label: StatusLabel;
  stage_display: string | null;
};

const RMS_ELT_TOTAL_STAGES = 3;

const TYPE_OPTIONS = [
  "ADDRESS_INTEGRATION",
  "ICM",
  "POLICY_FLY",
  "RATER",
  "RERUN_REPORT",
  "RMS",
];

const STATUS_OPTIONS = ["In Progress", "Pending", "Partially Running"];

const PRODUCTION_ENV = "production";

const defaultFilters: DataTableFilterMeta = {
  integration_type: { value: null, matchMode: FilterMatchMode.EQUALS },
  status_label: { value: null, matchMode: FilterMatchMode.EQUALS },
  env: { value: PRODUCTION_ENV, matchMode: FilterMatchMode.EQUALS },
};

const typeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
  <Dropdown
    value={options.value}
    options={TYPE_OPTIONS}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder="All Types"
    showClear
    style={{ minWidth: "150px" }}
  />
);

const statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
  <Dropdown
    value={options.value}
    options={STATUS_OPTIONS}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder="All Statuses"
    showClear
    style={{ minWidth: "150px" }}
  />
);

export const JobsInQueue = () => {
  const user = useLocalUser();
  const lobsForUser = (user?.lobs ?? []) as LOB[];
  const reports = useSafeReports(lobsForUser).data ?? [];
  const allUsers = useUsers().data?.all_company_users ?? [];
  const cancelJobs = useCancelJobs();

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);

  const reportIds = useMemo(() => reports.map((r) => r.report_id), [reports]);

  const companyNameByReportId = useMemo(
    () =>
      Object.fromEntries(
        reports.map((r) => [
          r.report_id,
          r.report_metadata.company_info.company_name,
        ])
      ),
    [reports]
  );

  const userNameById = useMemo(
    () =>
      Object.fromEntries(
        allUsers.map((u) => [u.id, u.full_name?.trim() || u.email])
      ),
    [allUsers]
  );

  const { data: jobs = [], isLoading } = useQueueJobs(reportIds, [
    "pending",
    "in_progress",
  ]);

  const rows: QueueRow[] = useMemo(() => {
    return jobs.map((job) => {
      const activeJobs = job.jobs.filter(
        (j) => j.status === "pending" || j.status === "in_progress"
      );
      const pendingPositions = activeJobs
        .map((j) => j.queue_position)
        .filter((p): p is number => p != null);
      const runningCount = activeJobs.filter(
        (j) => j.status === "in_progress"
      ).length;

      const min =
        pendingPositions.length > 0 ? Math.min(...pendingPositions) : null;
      const max =
        pendingPositions.length > 0 ? Math.max(...pendingPositions) : null;

      const position_display: string =
        pendingPositions.length === 0
          ? "—"
          : min === max
            ? `${min}`
            : `${min}–${max}`;

      const status_label: StatusLabel =
        runningCount > 0 && pendingPositions.length > 0
          ? "Partially Running"
          : pendingPositions.length === 0
            ? "In Progress"
            : "Pending";

      const stage_display =
        job.integration_type === "RMS_ELT"
          ? `Stage ${job.jobs.length} of ${RMS_ELT_TOTAL_STAGES}`
          : null;

      const triggered_by = job.initiating_user_id
        ? (userNameById[job.initiating_user_id] ?? job.initiating_user_id)
        : "—";

      return {
        ...job,
        company_name: companyNameByReportId[job.report_id] ?? "",
        triggered_by,
        min_position: min,
        position_display,
        status_label,
        stage_display,
      };
    });
  }, [jobs, companyNameByReportId, userNameById]);

  const envOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.env))).sort(),
    [rows]
  );

  const envFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
    <Dropdown
      value={options.value}
      options={envOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="All Envs"
      showClear
      style={{ minWidth: "150px" }}
    />
  );

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        // in_progress (null position) before pending (has position)
        if (a.min_position == null && b.min_position != null) return -1;
        if (a.min_position != null && b.min_position == null) return 1;
        if (a.min_position != null && b.min_position != null)
          return a.min_position - b.min_position;
        // both null (in_progress) — sort by started_at
        return a.started_at.localeCompare(b.started_at);
      }),
    [rows]
  );

  return (
    <div className={styles.page}>
      <Card title="Jobs in Queue">
        {isLoading ? (
          <div className={styles.centered}>
            <ProgressSpinner />
          </div>
        ) : (
          <DataTable
            value={sortedRows}
            filters={filters}
            filterDisplay="menu"
            onFilter={(e) => setFilters(e.filters)}
            emptyMessage="No jobs in queue"
            scrollable
            scrollHeight="calc(100vh - 300px)"
            stripedRows
          >
            <Column
              field="env"
              header="Env"
              filter
              filterElement={envFilterTemplate}
              showFilterMatchModes={false}
              sortable
              style={{ width: "160px" }}
            />
            <Column
              field="min_position"
              header="Position"
              sortable
              style={{ width: "100px" }}
              body={(row: QueueRow) => row.position_display}
            />
            <Column
              field="status_label"
              header="Status"
              filter
              filterElement={statusFilterTemplate}
              showFilterMatchModes={false}
              sortable
              style={{ width: "200px" }}
              body={(row: QueueRow) => (
                <div className={styles.statusCell}>
                  <Tag
                    value={row.status_label}
                    severity={
                      row.status_label === "Pending"
                        ? "warning"
                        : row.status_label === "Partially Running"
                          ? "warning"
                          : "info"
                    }
                  />
                  {row.stage_display && (
                    <span className={styles.stageDisplay}>
                      {row.stage_display}
                    </span>
                  )}
                </div>
              )}
            />
            <Column
              field="integration_type"
              header="Type"
              filter
              filterElement={typeFilterTemplate}
              showFilterMatchModes={false}
              sortable
              style={{ width: "200px" }}
            />
            <Column
              field="company_name"
              header="Company"
              sortable
              style={{ minWidth: "200px" }}
              body={(row: QueueRow) => (
                <Link to={ROUTES.DASHBOARD_REPORT(row.report_id)}>
                  {row.company_name}
                </Link>
              )}
            />
            <Column
              field="triggered_by"
              header="Triggered By"
              sortable
              style={{ width: "200px" }}
            />
            <Column
              field="started_at"
              header="Started"
              sortable
              style={{ width: "200px" }}
              body={(row: QueueRow) => formatTimestamp(row.started_at)}
            />
            <Column
              header="Jobs"
              style={{ width: "80px" }}
              body={(row: QueueRow) => row.jobs.length}
            />
            <Column
              header=""
              style={{ width: "120px" }}
              alignFrozen="right"
              frozen
              body={(row: QueueRow) => {
                const activeIds = row.jobs
                  .filter(
                    (j) => j.status === "pending" || j.status === "in_progress"
                  )
                  .map((j) => j.report_integration_id);
                const isCancellingThis =
                  cancelJobs.isPending &&
                  Array.isArray(cancelJobs.variables) &&
                  cancelJobs.variables.some((id) => activeIds.includes(id));
                return (
                  <Button
                    label="Cancel"
                    severity="warning"
                    size="small"
                    disabled={activeIds.length === 0 || cancelJobs.isPending}
                    loading={isCancellingThis}
                    onClick={() => cancelJobs.mutate(activeIds)}
                  />
                );
              }}
            />
          </DataTable>
        )}
      </Card>
    </div>
  );
};
